import get from "lodash.get";
import type { ImpositionSimulationDto } from "@/types/imposition";
import type { ICallApi } from "@/services/api-service/interface";

const IMPOSITION_SIMULATION_PATH_TEMPLATE =
  "/v1/erp-service/productItemValueDrafts/get-imposition-simulation-data/:signalRConnectionId";

export interface GetImpositionSimulationParams {
  signalRConnectionId: string;
  /** Query param only when the workflow has a product type — do not duplicate `signalRConnectionId` in query. */
  productType?: string | null;
}

/**
 * Path includes the connection id; optional `productType` is sent as the only query param when set.
 */
export function buildImpositionSimulationUrl(signalRConnectionId: string): string {
  return IMPOSITION_SIMULATION_PATH_TEMPLATE.replace(
    ":signalRConnectionId",
    encodeURIComponent(signalRConnectionId)
  );
}

/**
 * Normalizes nested API envelopes (`data.data.data`, `data.data`, or plain body).
 */
export function parseImpositionSimulationResponse(
  axiosPayload: unknown
): ImpositionSimulationDto | null {
  const inner =
    get(axiosPayload, "data.data.data") ??
    get(axiosPayload, "data.data") ??
    get(axiosPayload, "data") ??
    axiosPayload;
  if (inner && typeof inner === "object" && !Array.isArray(inner)) {
    return inner as ImpositionSimulationDto;
  }
  return null;
}

/**
 * GET imposition simulation. Uses the shared axios wrapper (`callApi` from `useGomakeAxios`).
 * Set `lockScreen` to false when the caller shows its own loading UI.
 */
export async function getImpositionSimulationData(
  callApi: ICallApi,
  params: GetImpositionSimulationParams,
  lockScreen = false
): Promise<{
  success: boolean;
  data: ImpositionSimulationDto | null;
  message?: string;
}> {
  const { signalRConnectionId, productType } = params;
  const url = buildImpositionSimulationUrl(signalRConnectionId);
  const query: Record<string, string> = {};
  if (productType != null && String(productType).trim() !== "") {
    query.productType = String(productType);
  }

  const result: any = await callApi("GET", url, query, lockScreen);
  if (!result?.success) {
    return {
      success: false,
      data: null,
      message:
        result?.message ||
        result?.msg ||
        result?.errors?.ErrorMessage ||
        "Request failed",
    };
  }
  const parsed = parseImpositionSimulationResponse(result.data);
  return {
    success: true,
    data: parsed ?? ({} as ImpositionSimulationDto),
    message: result?.message,
  };
}
